/**
 * Auth API — Session validation and authentication state.
 *
 * Port of AngularJS authService.js.
 *
 * The SPIGlass auth flow is cookie-based:
 * 1. User hits the app → server redirects to /Login if no auth cookie
 * 2. Login page does Windows Auth via https://authentication.{domain}/windows
 * 3. After auth, RoleMaster API returns user functions
 * 4. Cookie sent on every request via withCredentials: true
 *
 * This module checks whether the session cookie is still valid by calling
 * the RoleMaster endpoint (same as AngularJS authService.isAuthenticated).
 */

import axios from 'axios';
import { buildApiBase } from '@/lib/buildApiBase';
import { getMountConfig } from '@/mountConfig';
import { captureIntendedDestination } from '@/lib/loginRedirect';

// ============================================================================
// SESSION VALIDATION (port of authService.isAuthenticated)
// ============================================================================

/** Cached auth result to avoid hammering RoleMaster on every navigation. */
let cached: boolean | null = null;
let cachedFunctions: Record<string, unknown> = {};
let cachedAt = 0;
let inFlight: Promise<SessionCheckResult> | null = null;
const TTL_MS = 30_000; // 30 seconds — matches AngularJS

export interface SessionCheckResult {
  authed: boolean;
  functions: Record<string, unknown>;
}

/**
 * Validate the current session by calling the RoleMaster endpoint.
 *
 * Uses a standalone axios instance (NOT axiosClient) to avoid triggering
 * the 401 interceptor's login redirect during the initial auth check.
 *
 * @param force - Bypass the 30-second cache TTL
 */
export async function validateSession(force = false): Promise<SessionCheckResult> {
  const now = Date.now();

  // Return cached result if within TTL
  if (!force && cached !== null && now - cachedAt < TTL_MS) {
    return { authed: cached, functions: cachedFunctions };
  }

  // Deduplicate concurrent calls
  if (!force && inFlight) {
    return inFlight;
  }

  const url = `${buildApiBase('rolemasterv1')}/rolemaster/v1/getfunctionsbytoken`;

  inFlight = axios
    .get(url, { withCredentials: true, timeout: 15_000 })
    .then((response) => {
      cachedFunctions = response?.data?.functions ?? {};
      cached = true;
      cachedAt = Date.now();
      return { authed: true, functions: cachedFunctions } as SessionCheckResult;
    })
    .catch((err) => {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        cached = false;
        cachedFunctions = {};
        cachedAt = Date.now();
        return { authed: false, functions: {} } as SessionCheckResult;
      }

      // Unknown/temporary failure: keep previous cached value if we had one
      cachedAt = Date.now();
      const stillAuthed = cached === true;
      return {
        authed: stillAuthed,
        functions: stillAuthed ? cachedFunctions : {},
      } as SessionCheckResult;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * Clear the auth cache — used after 401 detection to force re-validation.
 * Port of authService.clearAuthCache().
 */
export function clearAuthCache(): void {
  cached = null;
  cachedFunctions = {};
  cachedAt = 0;
  inFlight = null;
}

/**
 * Quick synchronous check of the cached auth state.
 * Returns null if no cached value exists (never validated).
 */
export function isAuthenticatedCached(): boolean | null {
  return cached;
}

// ============================================================================
// LOGIN REDIRECT
// ============================================================================

/**
 * Build the login page URL.
 *
 * In embedded mode, this is the AngularJS login route.
 * In standalone mode, this is the React login page.
 */
export function getLoginUrl(): string {
  const { mode } = getMountConfig();
  // Standalone uses hash router: /#/login
  // Embedded uses AngularJS hash route: /#/Login
  return mode === 'standalone' ? '/#/login' : '/#/Login';
}

/**
 * Redirect to the login page.
 * Clears auth cache before redirecting.
 *
 * Also clears `window.__reloading401` so the next 401 in this tab fires a
 * fresh redirect — without this, the axios interceptor's anti-toast-spam guard
 * would suppress redirects after the first one (the hash-only nav doesn't
 * reload the page, so the flag persists).
 */
export function redirectToLogin(): void {
  clearAuthCache();
  // Capture where the user was trying to go so they can resume after sign-in
  // (Phase E coexistence: deep links from bookmarks / the AngularJS host
  // shouldn't get dropped on the floor by the auth bounce).
  captureIntendedDestination(window.location.hash);
  // (Removed: `sessionStorage.setItem('spi_401_detected', '1')` — this flag
  // was a signal to the AngularJS `$routeChangeStart` listener in
  // public/SPIGlassApp/app.js to suppress the auto bounce-back from /Login
  // to /Home/dashboard right after a 401 redirect. There is no AngularJS in
  // the standalone build, so the flag has no reader.)
  // Reset the 401 guard so future 401s in this tab can redirect again.
  // The 1.5s setTimeout in the axios interceptor has already fired by the time
  // this runs, so the anti-spam window is already over.
  try {
    delete window.__reloading401;
  } catch {
    window.__reloading401 = false;
  }
  window.location.href = getLoginUrl();
}
